import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import { useTracking } from "@/hooks/useTracking";
import { PHOTOS } from "@/assets/photos";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Camera, Sparkles, Truck, Monitor, Star, Shield, ChevronDown, MessageCircle, Phone, Mail, MapPin, Check } from "lucide-react";
import axios from "axios";
import logo from "@/assets/logo.png";
import BookingWizard from "@/components/BookingWizard";
import { useActivePromotion } from "@/hooks/useActivePromotion";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const carouselPhotos = [
  { src: PHOTOS.guests, label: "Mariage élégant" },
  { src: PHOTOS.roses, label: "Ambiance unique" },
  { src: PHOTOS.party, label: "Soirée festive" },
  { src: PHOTOS.hero, label: "Réception luxueuse" },
];

const reviews = [
  { name: "Sarah M.", event: "Mariage Juin 2024", text: "Un service impeccable ! Le photobooth a été la star de notre soirée. Tous nos invités ont adoré." },
  { name: "Karim B.", event: "Anniversaire Sept. 2024", text: "Ponctuel, professionnel et sympa. La borne est moderne et les impressions sont superbes." },
  { name: "Amina D.", event: "Baptême Oct. 2024", text: "La prise en main à distance était impeccable, tout s'est déroulé parfaitement. Photos magnifiques !" },
];

const faqData = [
  { q: "Quelle est la zone de livraison ?", a: "Toute l'Île-de-France, livraison et installation incluses. Pas de frais supplémentaires." },
  { q: "Combien de temps pour installer ?", a: "Le photobooth est autonome. L'installation prend environ 15 minutes et une prise en main à distance vous guide." },
  { q: "Les accessoires sont-ils inclus ?", a: "Oui, props et accessoires amusants inclus dans tous les forfaits." },
  { q: "Peut-on personnaliser les photos ?", a: "Oui, cadre personnalisé avec vos prénoms, logo ou thème." },
  { q: "Comment se passe le paiement ?", a: "Paiement sécurisé en ligne via Stripe, ou réservation par WhatsApp. Le photobooth est autonome, aucun technicien sur place." },
];

// ——— Animated Wrapper ———
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

// ——— Countdown ———
const Countdown = ({ endDate }) => {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const target = new Date(endDate).getTime();
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);

  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="flex gap-4 justify-center mt-4">
      {[
        { v: pad(time.d), l: "jours" },
        { v: pad(time.h), l: "heures" },
        { v: pad(time.m), l: "min" },
        { v: pad(time.s), l: "sec" },
      ].map((t, i) => (
        <div key={i} className="text-center">
          <span className="font-['Playfair_Display'] text-2xl font-bold text-[#b8977e]">{t.v}</span>
          <p className="font-['DM_Sans'] text-[10px] text-white/50 uppercase tracking-wider mt-1">{t.l}</p>
        </div>
      ))}
    </div>
  );
};

// ——— FAQ Item ———
const FaqItem = ({ q, a, isOpen, onClick }) => (
  <div className="border-b border-[#eee]">
    <button onClick={onClick} className="w-full flex items-center justify-between py-5 text-left group">
      <span className="font-['DM_Sans'] font-medium text-[#1a1a1a] text-sm pr-4">{q}</span>
      <span className={`text-[#b8977e] text-xl font-light flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-45" : ""}`}>+</span>
    </button>
    <div
      className="overflow-hidden transition-all duration-300"
      style={{ maxHeight: isOpen ? "200px" : "0", opacity: isOpen ? 1 : 0 }}
    >
      <p className="font-['DM_Sans'] text-[#666] text-sm pb-5">{a}</p>
    </div>
  </div>
);

// ——— Main ———
const LandingPageMeta = () => {
  useTracking({ page: "landing" });

  const { promo: activePromotion } = useActivePromotion();
  const [openFaq, setOpenFaq] = useState(null);
  const [carouselApi, setCarouselApi] = useState(null);
  const [carouselCurrent, setCarouselCurrent] = useState(0);
  const [apiReviews, setApiReviews] = useState([]);

  useEffect(() => {
    fetchApiReviews();
  }, []);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setCarouselCurrent(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    return () => carouselApi.off("select", onSelect);
  }, [carouselApi]);

  const fetchApiReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/active`);
      const active = Array.isArray(res.data) ? res.data : [];
      if (active.length > 0) setApiReviews(active);
    } catch (err) { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-white font-['DM_Sans']" data-testid="landing-page">

      {/* ——— 1. NAVBAR ——— */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm" data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="SelfieBooth Event" style={{ height: "48px", width: "auto" }} data-testid="nav-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </Link>
          <a
            href="tel:+33679393429"
            className="font-semibold border-2 border-[#b8977e] text-[#b8977e] px-5 py-2 rounded-full hover:bg-[#b8977e] hover:text-white transition-colors text-sm inline-flex items-center gap-2"
            data-testid="nav-phone-btn"
          >
            <Phone className="w-4 h-4" /> Nous appeler
          </a>
        </div>
      </nav>

      {/* ——— 2. HERO ——— */}
      <section className="relative pt-[72px]" data-testid="landing-hero">
        <div className="relative" style={{ height: "calc(90vh - 72px)", minHeight: "540px" }}>
          <img src={PHOTOS.hero} alt="Salle de mariage luxueuse" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.55))" }} />

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
            {/* Badge urgence */}
            <div
              className="bg-[#b8977e] text-white text-sm font-medium px-5 py-2 rounded-full mb-8 shadow-lg"
              style={{ animation: "pulse-badge 2s ease-in-out infinite" }}
              data-testid="urgency-badge"
            >
              Offre limitée — Places disponibles ce mois-ci
            </div>

            <h1
              className="font-['Playfair_Display'] font-bold text-white leading-tight max-w-4xl mb-5"
              style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)" }}
            >
              Votre Photobooth Premium pour un Événement Inoubliable
            </h1>

            <p className="text-white/85 text-base sm:text-lg max-w-2xl mb-6">
              Mariage · Anniversaire · Corporate · Baptême — Île-de-France
            </p>

            <div className="flex items-center gap-1.5 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#f4b942] text-[#f4b942]" />
              ))}
              <span className="text-white/70 text-sm ml-2">150+ événements · 98% de clients satisfaits</span>
            </div>

            <a
              href="#devis"
              className="bg-[#b8977e] text-white font-semibold px-10 py-4 rounded-full text-base hover:bg-[#a68770] transition-colors shadow-xl"
              style={{ boxShadow: "0 8px 30px rgba(184, 151, 126, 0.4)" }}
              data-testid="hero-cta"
            >
              Obtenir mon devis gratuit →
            </a>

            <p className="text-white/50 text-xs mt-4 flex items-center gap-3">
              <span>✓ Gratuit</span><span>✓ Sans engagement</span><span>✓ Réponse sous 2h</span>
            </p>
          </div>

          {/* Scroll arrow */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10" style={{ animation: "bounce-arrow 1.5s ease-in-out infinite" }}>
            <ChevronDown className="w-6 h-6 text-white/50" />
          </div>
        </div>
      </section>

      {/* ——— 3. PROMO DYNAMIQUE ——— */}
      {activePromotion && (
        <section className="bg-[#1a1a1a] py-8" data-testid="promo-section">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl font-bold text-white mb-2">
              {activePromotion.title}
            </h3>
            <div className="flex items-center justify-center gap-4 mb-2">
              {activePromotion.code && (
                <span className="font-mono font-bold text-[#b8977e] bg-[#b8977e]/10 border border-[#b8977e]/30 px-4 py-1.5 rounded text-sm tracking-wider">
                  {activePromotion.code}
                </span>
              )}
              <span className="font-['Playfair_Display'] text-2xl font-bold text-[#b8977e]">
                {activePromotion.discount_type === "percentage"
                  ? `-${activePromotion.discount_value}%`
                  : `-${activePromotion.discount_value}€`}
              </span>
            </div>
            {activePromotion.end_date && <Countdown endDate={activePromotion.end_date} />}
          </div>
        </section>
      )}

      {/* ——— 4. CARROUSEL PHOTOS ——— */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <AnimatedSection className="text-center mb-12">
            <span className="text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Nos réalisations</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-4">
              Ils nous ont choisi pour leurs plus beaux moments
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <Carousel
              setApi={setCarouselApi}
              opts={{ align: "start", loop: true }}
              plugins={[Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })]}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {carouselPhotos.map((photo, i) => (
                  <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <div className="relative overflow-hidden rounded-[1.5rem] group">
                      <img
                        src={photo.src}
                        alt={photo.label}
                        className="w-full h-[380px] object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-5">
                        <span className="text-white font-medium text-sm">{photo.label}</span>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg border-0 h-10 w-10 sm:h-12 sm:w-12" />
              <CarouselNext className="absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg border-0 h-10 w-10 sm:h-12 sm:w-12" />
            </Carousel>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {carouselPhotos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => carouselApi?.scrollTo(i)}
                  className={`rounded-full transition-all duration-300 ${carouselCurrent === i ? "w-8 h-2.5 bg-[#b8977e]" : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"}`}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ——— 5. POURQUOI NOUS ——— */}
      <section className="py-16 md:py-20 bg-[#f5ede8]">
        <div className="max-w-[900px] mx-auto px-6">
          <AnimatedSection className="text-center mb-12">
            <span className="text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Nos atouts</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-4">
              Pourquoi choisir SelfieBooth Event ?
            </h2>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: Camera, title: "Photos HD instantanées", desc: "Impression en 10 secondes, qualité professionnelle garantie" },
              { icon: Sparkles, title: "Accessoires & Props inclus", desc: "Lunettes, chapeaux, pancartes fun pour tous vos invités" },
              { icon: Truck, title: "Livraison & Installation IDF", desc: "Livraison et installation incluses en Île-de-France" },
              { icon: Monitor, title: "Galerie digitale offerte", desc: "Toutes les photos disponibles en ligne après l'événement" },
            ].map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="bg-white rounded-[1.5rem] p-7 hover:shadow-lg hover:shadow-[#b8977e]/10 transition-shadow">
                  <item.icon className="w-10 h-10 text-[#b8977e] mb-4" />
                  <h3 className="font-semibold text-[#1a1a1a] mb-2">{item.title}</h3>
                  <p className="text-[#666] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ——— 6. WIZARD DE RÉSERVATION (3 étapes) ——— */}
      <section id="devis" className="py-16 md:py-24" style={{ background: "#faf8f5" }} data-testid="devis-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center mb-10">
            <span className="text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Réservation en ligne</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-3 mb-2">
              Réservez votre Photobooth
            </h2>
            <p className="text-[#666] text-sm sm:text-base">
              Choisissez votre forfait, personnalisez vos options et payez en toute sécurité
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <div
              className="rounded-[2rem] overflow-hidden"
              style={{
                background: "#ffffff",
                border: "1px solid #e8d5c4",
                boxShadow: "0 4px 40px rgba(0,0,0,0.06)",
              }}
              data-testid="wizard-card"
            >
              <BookingWizard embedded={true} />
            </div>
          </AnimatedSection>

          <p className="text-center text-xs text-[#999] flex items-center justify-center gap-1.5 mt-6">
            <Shield className="w-3.5 h-3.5" /> Paiement 100% sécurisé · Données protégées · Devis sans engagement
          </p>
        </div>
      </section>

      {/* ——— 7. TÉMOIGNAGES ——— */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <AnimatedSection>
            <span className="text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Témoignages</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mt-4 mb-14">
              Ce que disent nos clients
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {(apiReviews.length > 0
              ? apiReviews.slice(0, 6).map(r => ({ name: r.client_name, event: "", text: r.review_text, rating: r.rating }))
              : reviews.map(r => ({ ...r, rating: 5 }))
            ).map((r, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="bg-white rounded-[1.5rem] p-7 text-left shadow-lg">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, si) => (
                      <Star key={si} className={`w-4 h-4 ${si < r.rating ? "fill-[#f4b942] text-[#f4b942]" : "text-gray-300"}`} />
                    ))}
                  </div>
                  <p className="font-['Cormorant_Garamond'] italic text-[#333] text-base leading-relaxed mb-5">
                    "{r.text}"
                  </p>
                  <p className="font-semibold text-[#1a1a1a] text-sm">{r.name}</p>
                  {r.event && <p className="text-[#999] text-xs">{r.event}</p>}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ——— 8. FAQ ——— */}
      <section className="py-16 md:py-20 bg-[#fafafa]">
        <div className="max-w-2xl mx-auto px-6">
          <AnimatedSection className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
              Questions fréquentes
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            {faqData.map((faq, i) => (
              <FaqItem
                key={i}
                q={faq.q}
                a={faq.a}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* ——— 9. CTA FINAL ——— */}
      <section className="py-16 md:py-24 bg-[#f5ede8] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatedSection>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-5">
              Prêt à créer des souvenirs inoubliables ?
            </h2>
            <p className="text-[#777] mb-10">
              Répondez à quelques questions — votre devis arrive en 2h
            </p>
            <a
              href="#devis"
              className="inline-block font-semibold bg-[#b8977e] text-white px-10 py-4 rounded-full text-base hover:bg-[#a68770] transition-colors shadow-lg"
              style={{ boxShadow: "0 8px 30px rgba(184,151,126,0.3)" }}
            >
              Je veux mon devis gratuit →
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* ——— Legacy footer removed: unified global <Footer /> is mounted at App level (Aug 2026) ——— */}

      {/* ——— WhatsApp Floating ——— */}
      <a
        href="https://wa.me/33679393429"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform"
        data-testid="whatsapp-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </a>

      {/* ——— Animations CSS ——— */}
      <style>{`
        @keyframes pulse-badge {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes bounce-arrow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
      `}</style>
    </div>
  );
};

export default LandingPageMeta;
